/**
 * PATH: packages/bills/src/billsAging.ts
 * FILE_ID: BILLS-PKG-AGING-001
 * PURPOSE: ONE canonical aging / overdue / priority / AP-lane vocabulary
 *          for bills, reused by the List, Approvals and Cash-out schedule
 *          so the three surfaces can never disagree about "what's overdue"
 *          or "what's high priority".
 *
 * WHY: Before this, PaymentSchedule.tsx had its own bucket maths,
 *      BillingApprovalsPage.tsx had its own `priorityFor`, and BillsTable
 *      had neither — three implementations drifting apart (operator
 *      2026-06-19: "the list view and approvals is fucked … there is no
 *      AP section"). A shared pure module kills the drift and unblocks the
 *      live AP cashflow pivot (p2b).
 *
 * FLOW:
 *   ┌──────────────┐   ┌───────────────────────────┐   ┌────────────────┐
 *   │ BillListRow   │ → │ daysUntilDue / bucket /    │ → │ List · Approvals│
 *   │ (due, status, │   │ overdue / priority / lane  │   │ · Schedule ·    │
 *   │  paid)        │   │ (pure I→O, now-injectable) │   │ AP cashflow     │
 *   └──────────────┘   └───────────────────────────┘   └────────────────┘
 *
 * DEPENDENCIES: types.ts only (pure module — keep it dependency-free so it
 *   stays trivially unit-testable and importable from any surface).
 */

import type { BillStatus, BillPaymentStatus } from './types';

/* ───────────────────── date primitives (I→O) ───────────────────── */

/** Midnight of the given date in local time (new Date — never mutates). */
export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** `d + n` days as a new Date (never mutates the input). */
export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/**
 * Whole days from `now` (midnight) to the due date (midnight).
 * Negative ⇒ overdue, 0 ⇒ due today, positive ⇒ future. `null` when the
 * date is missing or unparseable so callers can route to "no due date".
 */
export function daysUntilDue(
  due: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!due) return null;
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return null;
  const ms = startOfDay(d).getTime() - startOfDay(now).getTime();
  return Math.round(ms / 86_400_000);
}

/** Tolerant numeric parse for DRF's stringified decimals. Non-numeric → 0. */
export function parseAmount(v: string | number | null | undefined): number {
  if (v == null || v === '') return 0;
  const n = typeof v === 'string' ? Number(v) : v;
  return Number.isFinite(n) ? n : 0;
}

/* ───────────────────── aging buckets ───────────────────── */

export type AgingBucketKey =
  | 'overdue'
  | 'today'
  | 'this_week'
  | 'this_month'
  | 'later'
  | 'no_due_date';

export type AgingTone = 'rose' | 'amber' | 'sky' | 'slate' | 'muted';

export interface AgingBucketMeta {
  key: AgingBucketKey;
  label: string;
  rangeLabel: string;
  tone: AgingTone;
}

/** Ordered, most-urgent-first — the canonical render order everywhere. */
export const AGING_BUCKETS: readonly AgingBucketMeta[] = [
  { key: 'overdue', label: 'Overdue', rangeLabel: 'Past due — pay first', tone: 'rose' },
  { key: 'today', label: 'Due today', rangeLabel: 'Today', tone: 'amber' },
  { key: 'this_week', label: 'This week', rangeLabel: 'Next 7 days', tone: 'amber' },
  { key: 'this_month', label: 'This month', rangeLabel: '8–30 days', tone: 'sky' },
  { key: 'later', label: 'Later', rangeLabel: '30+ days out', tone: 'slate' },
  { key: 'no_due_date', label: 'No due date', rangeLabel: 'Set due date to schedule', tone: 'muted' },
] as const;

export const AGING_BUCKET_META: Record<AgingBucketKey, AgingBucketMeta> =
  Object.fromEntries(AGING_BUCKETS.map((b) => [b.key, b])) as Record<
    AgingBucketKey,
    AgingBucketMeta
  >;

/** Map a due date → its aging bucket using the shared thresholds
 *  (overdue / today / ≤7d / ≤30d / later / none). */
export function agingBucketKey(
  due: string | null | undefined,
  now: Date = new Date(),
): AgingBucketKey {
  const days = daysUntilDue(due, now);
  if (days === null) return 'no_due_date';
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  if (days <= 7) return 'this_week';
  if (days <= 30) return 'this_month';
  return 'later';
}

/* ───────────────────── payable / AP-lane state ───────────────────── */

/** Minimal structural shape the aging helpers read off a bill row. */
export interface AgingRow {
  due_date: string | null;
  total?: string | null;
  status?: BillStatus;
  payment_status?: BillPaymentStatus | null;
}

/**
 * Is this bill still a future cash-out? Paid bills are settled and
 * rejected (`failed`) bills are dead — both drop out of every AP view.
 */
export function isPayable(row: AgingRow): boolean {
  if (row.payment_status === 'paid') return false;
  if (row.status === 'failed') return false;
  return true;
}

/** Overdue = still payable AND its due date is in the past. A paid bill
 *  is never overdue, however late it was. */
export function isOverdue(row: AgingRow, now: Date = new Date()): boolean {
  if (!isPayable(row)) return false;
  const days = daysUntilDue(row.due_date, now);
  return days !== null && days < 0;
}

/**
 * AP approval lane for the cashflow split:
 *   - 'awaiting'  → pending / extracted (not yet human-approved)
 *   - 'approved'  → reviewed / pushed (approved to pay / in Xero)
 *   - null        → excluded from AP (paid or rejected)
 * Mirrors the plan's "extracted/pending = awaiting vs reviewed =
 * approved-to-pay", with `pushed` folded into approved (it's past the
 * approval gate and still owed until paid).
 */
export type ApprovalLane = 'awaiting' | 'approved';

export function approvalLane(row: AgingRow): ApprovalLane | null {
  if (!isPayable(row)) return null;
  switch (row.status) {
    case 'pending':
    case 'extracted':
      return 'awaiting';
    case 'reviewed':
    case 'pushed':
      return 'approved';
    default:
      return 'awaiting';
  }
}

/* ───────────────────── priority ───────────────────── */

export type BillPriority = 'high' | 'medium' | 'low';

/**
 * Unified priority heuristic (was duplicated/absent across surfaces).
 * Overdue and imminently-due bills outrank everything; large amounts
 * escalate the rest. `now` is injectable for deterministic tests.
 *
 * high   : overdue, or due within 3 days, or amount > $5,000
 * medium : due within 14 days, or amount > $1,000
 * low    : everything else
 */
export function billPriority(row: AgingRow, now: Date = new Date()): BillPriority {
  if (isPayable(row)) {
    const days = daysUntilDue(row.due_date, now);
    if (days !== null) {
      if (days < 3) return 'high';
      if (days < 14) return 'medium';
    }
  }
  const amt = parseAmount(row.total);
  if (amt > 5000) return 'high';
  if (amt > 1000) return 'medium';
  return 'low';
}

/** Sort comparator: high → low, then most-overdue / soonest-due first. */
export function comparePriority(
  a: AgingRow,
  b: AgingRow,
  now: Date = new Date(),
): number {
  const rank: Record<BillPriority, number> = { high: 0, medium: 1, low: 2 };
  const pa = rank[billPriority(a, now)];
  const pb = rank[billPriority(b, now)];
  if (pa !== pb) return pa - pb;
  const da = daysUntilDue(a.due_date, now);
  const db = daysUntilDue(b.due_date, now);
  // Nulls (no due date) sort last within the same priority band.
  if (da === null && db === null) return 0;
  if (da === null) return 1;
  if (db === null) return -1;
  return da - db;
}

/* ───────────────────── overdue pill copy ───────────────────── */

export interface DuePill {
  label: string;
  tone: AgingTone;
}

/** Human due-date pill ("3d overdue" / "due today" / "in 5d" / "no date"),
 *  with the matching tone — shared by the List + Schedule rows. */
export function dueDatePill(
  due: string | null | undefined,
  now: Date = new Date(),
): DuePill {
  const days = daysUntilDue(due, now);
  if (days === null) return { label: 'no date', tone: 'muted' };
  if (days < 0) return { label: `${Math.abs(days)}d overdue`, tone: 'rose' };
  if (days === 0) return { label: 'due today', tone: 'amber' };
  if (days <= 7) return { label: `in ${days}d`, tone: 'amber' };
  if (days <= 30) return { label: `in ${days}d`, tone: 'sky' };
  return { label: `in ${days}d`, tone: 'slate' };
}
